import React from 'react';
import { useData } from '../DataContext';
import { Disc, Calendar, MessageSquare, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import useScrambleText from '../../hooks/useScrambleText';
import ElectricCard from '../ElectricCard';

const StatCard = ({ icon: Icon, label, value, color }) => {
  // Map color classes to hex values
  const colorMap = {
    'text-primary': '#00ff41',
    'text-secondary': '#9333ea',
    'text-accent': '#e91e63',
    'text-green-400': '#4ade80'
  };
  
  return (
    <ElectricCard color={colorMap[color] || '#00ff41'}>
      <div className="bg-card/50 backdrop-blur-sm p-6 h-full relative overflow-hidden group">
        <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity ${color}`}>
          <Icon className="w-24 h-24" />
        </div>
        <div className="relative z-10">
          <div className={`w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-4 ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
          <h3 className="text-3xl font-heading font-bold text-white mb-1">{value}</h3>
          <p className="text-muted-foreground text-sm uppercase tracking-wider">{label}</p>
        </div>
      </div>
    </ElectricCard>
  );
};

const AdminDashboard = () => {
  const { releases, events, messages } = useData();
  const h1Ref = useScrambleText("Command Center");

  return (
    <div className="space-y-8">
      <div>
        <h1 ref={h1Ref} className="text-3xl font-heading font-bold korov-gradient-text mb-2 cursor-pointer">Command Center</h1>
        <p className="text-muted-foreground">System Overview and Statistics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          icon={Disc} 
          label="Total Releases" 
          value={releases.length} 
          color="text-primary" 
        />
        <StatCard 
          icon={Calendar} 
          label="Upcoming Events" 
          value={events.length} 
          color="text-secondary" 
        />
        <StatCard 
          icon={MessageSquare} 
          label="New Messages" 
          value={messages.length} 
          color="text-accent" 
        />
        <StatCard 
          icon={TrendingUp} 
          label="System Status" 
          value="ONLINE" 
          color="text-green-400" 
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ElectricCard color="#e91e63">
          <div className="bg-card/50 backdrop-blur-sm p-6 h-full">
            <h3 className="text-xl font-heading font-bold text-white mb-6">Recent Activity</h3>
            <div className="space-y-4">
              {messages.slice(0, 3).map((msg) => (
                <div key={msg.id} className="flex items-center gap-4 p-4 bg-white/5 rounded border border-white/5">
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <div>
                    <p className="text-sm text-white">New message from <span className="text-primary">{msg.name}</span></p>
                    <p className="text-xs text-muted-foreground">{msg.date}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded border border-white/5">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <div>
                  <p className="text-sm text-white">System updated to version <span className="text-primary">2.0.4</span></p>
                  <p className="text-xs text-muted-foreground">Just now</p>
                </div>
              </div>
            </div>
          </div>
        </ElectricCard>

        <ElectricCard color="#9333ea">
          <div className="bg-card/50 backdrop-blur-sm p-6 h-full flex flex-col justify-center items-center text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative z-10">
              <h3 className="text-xl font-heading font-bold text-white mb-2">Quick Actions</h3>
              <p className="text-muted-foreground mb-6">Manage your content directly</p>
              <div className="flex gap-4">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-black">Add Release</Button>
                <Button variant="outline" className="border-secondary/50 text-secondary hover:bg-secondary hover:text-white">Upload Photo</Button>
              </div>
            </div>
          </div>
        </ElectricCard>
      </div>
    </div>
  );
};

export default AdminDashboard;
